/**
 * The audit-log `action` vocabulary — **the words a customer reads about their own
 * organization.**
 *
 * ## Why this is in `packages/shared` and not in `apps/api`, where it was written
 *
 * It shipped in c27 as `apps/api/src/audit.ts`, beside the writer, which was right while
 * the only consumer was the writer. c36 gives it a second consumer in a different app:
 * the audit-log screen renders these strings, groups by them and filters on them, and
 * `apps-never-import-apps` means the SPA cannot reach `apps/api` to get them. A copy in
 * `apps/app` would be the failure this repository has now recorded under several names —
 * two spellings of one vocabulary, drifting silently, with the drift visible only as a
 * row that renders as a raw token in somebody's dashboard.
 *
 * So it moves, and the writer stays: `recordAudit` is `packages/db`'s (`src/audit.ts`), and
 * the API and the worker both call it inside their own transactions. **What crosses the
 * boundary is the vocabulary, not the mechanism.**
 *
 * ## These are product copy, not internal identifiers
 *
 * U2 ruled that *"all logs tracked for assessment"* means the **per-org audit log** read
 * by tenants, not a platform-operator surface. So these strings are read by customers, in
 * their own dashboard, and **renaming one later changes what a historical row appears to
 * say** — the rows already written do not move. They are a product vocabulary from the
 * first row written, not an internal set to be tidied later.
 *
 * `resource.verb`, both halves from the authorization matrix, so a capability and its
 * audit record cannot drift apart: if `authorize` gates it, the log names it the same way.
 *
 * ## Some of these have no writer, and that is a state to see rather than a gap to close
 *
 * Measured at c36 rather than assumed, eight had no writer, each belonging to a surface **R9
 * deferred out of Phase 2**. U60 and U61 gave `org.update` writers, and U64 gave the three
 * `member.*` words theirs. Five remain: `apiKey.create`, `apiKey.revoke`,
 * `workspace.create`, `workspace.update` and `workspace.delete`. §14's R9 note says
 * `apiKey.*` exist "only as audit vocabulary", with `actor_api_key_id` an unwritten column.
 * `0040` added three more, `import.*`, ahead of the units that write them.
 *
 * They are kept, deliberately, for the reason the vocabulary exists at all: the
 * alternative is adding them in Phase 5 alongside the routes, which is the moment nobody
 * is thinking about what a customer will read. But **a vocabulary entry with no writer is
 * dead weight and should be visible as such** — the same condition `problem.ts`'s own
 * header states about registered codes nothing emits, and the same one c33 found had gone
 * unnoticed for five commits. {@link AUDIT_ACTIONS_WITHOUT_WRITER} is that visibility,
 * and it is asserted against the tree rather than maintained by memory.
 *
 * The distinction matters for how they are tested. An action with a writer can be driven
 * through the API and read back through the endpoint — a **round trip**. An action without
 * one can only be written by calling `recordAudit` directly, which says nothing about any
 * route and everything about whether the column will hold the string. That second thing is
 * a **storability** claim, and it is worth making — a value in this list that the database
 * refuses is a Phase 5 outage in a Phase 2 constant — but it must not be reported as the
 * first.
 */
/**
 * What happened, in the vocabulary tenants read.
 *
 * Extended as capabilities land — each new entry is a product decision about a word a
 * customer will see, which is why they are enumerated rather than accepting any string.
 * The database holds the same list as a `CHECK` (`0014`), set-equal to this constant by
 * `packages/db/test/constraints.test.ts`, so adding a word here without a migration is a
 * test failure rather than a value that inserts and then cannot be read back by a client
 * that does not know it.
 */
export const AUDIT_ACTIONS = [
    "member.invite",
    "member.remove",
    "member.role_change",
    // Words about an invite take `invite.`, and words about ownership take `ownership.` — the
    // resource `PERMISSIONS` names. `member.invite` predates the rule and stays — Drift §U64.
    "invite.revoke",
    "ownership.transfer",
    "apiKey.create",
    "apiKey.revoke",
    /**
     * The organization was brought into existence, and somebody became its owner.
     *
     * The first row in every tenant's log, written by the bootstrap in the same transaction
     * as the `organizations` row itself. Named apart from `org.update` because a customer
     * reading their own audit log — or a reviewer reading it during a security assessment —
     * needs to see where the trail *starts*: an `org.update` at the top of an empty log
     * would say something changed without saying that everything did.
     */
    "org.create",
    "org.update",
    "workspace.create",
    "workspace.update",
    "workspace.delete",
    "product.create",
    "product.update",
    "product.publish",
    "product.retire",
    "link.create",
    "link.update",
    // Reversible, and an editor may do it (ruled 2026-08-06). Named separately from
    // link.update because a customer reading their own audit log should see what happened,
    // not a diff they have to interpret.
    "link.deactivate",
    // Putting one back. `link.update` would be true and useless — the reader wants to know a
    // link started resolving again, not that two columns changed.
    "link.activate",
    // Not reversible, so it stops at admin.
    "link.delete",
    // Named separately for the same reason `link.deactivate` is, and it earns it harder:
    // `position` is the resolver's final tiebreak, so reordering is a **resolution-affecting**
    // write that looks like a display preference. A customer reading their own log should see
    // that the order changed, not a `link.update` row whose diff is two integers.
    "link.reorder",
    "recall.publish",
    // A scope correction, which is widen-only. Named apart from `recall.publish` because a
    // customer reading their own log — or a regulator reading the evidence export beside it —
    // needs to see that the scope changed and when, not one `recall.publish` row per hazard
    // whose diff they have to interpret. The lots it added are in the `scope_widened` event.
    "recall.widen",
    "recall.lift",
    // The tenant pointed a hostname at us. Named apart from `org.update` because the act a
    // customer remembers is adding a domain, and because it commits their DNS.
    "domain.create",
    // Taken out of service, reversibly — `0023` keeps the row and `first_live_at`, and
    // re-adding the hostname revives it. `deactivate` is the matrix action for that shape, so
    // `domain.delete` would be a word the log shows for a row nothing deleted.
    "domain.deactivate",
    /**
     * The tenant's DNS record was found pointing at our edge — `→ verifying`.
     *
     * **Not the recheck.** A poll is not an act and writes nothing; what belongs in a
     * customer's log is that their record started answering, which is the world changing
     * rather than a person acting.
     *
     * **Named for the outcome, not `domain.status_change`**, which was the first draft. This
     * file's own argument against `link.update` kills it: *"a customer reading their own log
     * should see what happened, not a diff they have to interpret."* A row saying
     * `status_change` reintroduces exactly that, one word later.
     *
     * `domain.live` joined this list at U13/C3, with the worker that writes it — see its own
     * entry below and {@link AUDIT_ACTIONS_WITHOUT_WRITER}.
     */
    "domain.dns_verified",
    /** The lookup found no record, or one pointing somewhere else — `→ error`. */
    "domain.dns_failed",
    /**
     * The hostname served a real scan through the public edge — `→ live`, and the moment
     * `first_live_at` is stamped.
     *
     * **The word a customer waited for.** Named for the outcome like its two siblings above:
     * the log should say their domain went live, not that a synthetic probe returned 307.
     *
     * Written by `apps/worker`'s probe job and by nothing else. It arrived at U13/C3 with
     * that writer, on the trigger `drizzle/0024`'s header named — which is why it was absent
     * from `AUDIT_ACTIONS` until then rather than reserved in advance.
     */
    "domain.live",
    // A company prefix's ladder, one word per rung — `0028`. The claim and the evidence
    // wrote `org.update` until then, which U22 read on screen as three identical rows.
    "prefix.claim",
    "prefix.evidence",
    // The reviewer's two verdicts, written only by `reviewPrefix()` with a NULL actor —
    // Decisions/2026-09-23 The Reviewer Write Has One Door.
    "prefix.verify",
    "prefix.reject",
    // The organisation's subscription moved — its plan, its Stripe status or its period —
    // written by `packages/db/src/billing/transition.ts` alone, from a Stripe event (`0030`).
    "subscription.changed",
    // An import was created, applied or cancelled — `0040`. Written by U73 and U77.
    "import.create",
    "import.apply",
    "import.cancel",
];
/**
 * The entries nothing in the product writes yet — see the header.
 *
 * **This list is a measurement, not a policy.** It is asserted against `apps/api/src` by
 * `apps/api/test/audit-vocabulary.test.ts`, in both directions: every action named here
 * must have no emitter, and every action *not* named here must have one. So it goes red
 * when Phase 5 ships member management and forgets to remove the entry, and it goes red
 * the day somebody adds a word with no writer and does not say so.
 *
 * The direction that matters is the second one. A vocabulary quietly growing entries
 * nothing emits is how a registry becomes decoration, and this project has watched that
 * happen once already — `UNAUTHENTICATED`, `FORBIDDEN` and `NOT_A_MEMBER` sat in
 * `PROBLEMS` from c28 with nothing emitting them until c33 went looking.
 */
export const AUDIT_ACTIONS_WITHOUT_WRITER = [
    "apiKey.create",
    "apiKey.revoke",
    "workspace.create",
    "workspace.update",
    "workspace.delete",
];
/** Is this string one of the words this product writes? */
export function isAuditAction(value) {
    return AUDIT_ACTIONS.includes(value);
}
//# sourceMappingURL=audit.js.map